class CacheManager<T = any> {
  // 最大缓存容量
  private readonly maxSize: number;
  // 缓存存储，使用Map数据结构，键为字符串，值为泛型T
  private readonly cache: Map<string, T>;
  // 访问顺序记录，用于实现LRU（最近最少使用）缓存策略
  private accessOrder: string[];

  constructor(maxSize = 5) {
    this.maxSize = maxSize;
    this.cache = new Map<string, T>();
    this.accessOrder = []; // 用于LRU淘汰策略
  }

  // 获取缓存
  get(key: string): T | null {
    if (this.cache.has(key)) {
      // 更新访问顺序
      this.accessOrder = this.accessOrder.filter((k) => k !== key);
      this.accessOrder.push(key);
      return this.cache.get(key) as T;
    }
    return null;
  }

  // 设置缓存
  set(key: string, value: T): void {
    // 如果缓存已满，移除最久未使用的
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      const oldestKey = this.accessOrder.shift();
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, value);

    // 更新访问顺序
    if (!this.accessOrder.includes(key)) {
      this.accessOrder.push(key);
    }
  }

  // 判断是否为第一次访问
  isFirstAccess(key: string): boolean {
    return !this.cache.has(key);
  }

  // 清除指定缓存
  clear(key: string): void {
    this.cache.delete(key);
    this.accessOrder = this.accessOrder.filter((k) => k !== key);
  }

  // 清除所有缓存
  clearAll(): void {
    this.cache.clear();
    this.accessOrder = [];
  }

  // 获取所有缓存键
  getKeys(): string[] {
    return Array.from(this.cache.keys());
  }

  // 获取缓存数量
  getSize(): number {
    return this.cache.size;
  }

  // 检查缓存是否已满
  isFull(): boolean {
    return this.cache.size >= this.maxSize;
  }

  // 获取最久未使用的键（LRU策略）
  getOldestKey(): string | null {
    return this.accessOrder.length > 0 ? this.accessOrder[0] : null;
  }

  // 获取最近使用的键（LRU策略）
  getNewestKey(): string | null {
    return this.accessOrder.length > 0 ? this.accessOrder[this.accessOrder.length - 1] : null;
  }

  // 检查键是否存在
  has(key: string): boolean {
    return this.cache.has(key);
  }

  // 获取所有缓存值
  getValues(): T[] {
    return Array.from(this.cache.values());
  }

  // 获取所有缓存条目
  getEntries(): Array<[string, T]> {
    return Array.from(this.cache.entries());
  }
  /**
   * 获取所有缓存
   * @returns
   */
  getCatchs() {
    return this.cache;
  }
}

// 全局缓存实例 - 使用 number 类型作为值，因为你在代码中存储的是 1
export const cacheManager = new CacheManager<number>(5);
